/**
 * Shared utilities for fetching PDFs safely in the browser.
 *
 * Goals:
 * - Avoid confusing pdf-lib errors like "No PDF header found" by validating responses.
 * - Avoid cached 403 responses for short-lived pre-signed S3 URLs by using cache: "no-store".
 * - Provide structured errors for UI layers to implement retry/refresh logic.
 */

export interface PdfFetchErrorData {
  url: string;
  status?: number;
  statusText?: string;
  contentType?: string;
  bodySnippet?: string;
  isPresignedS3Url: boolean;
}

export class PdfFetchError extends Error {
  public readonly data: PdfFetchErrorData;

  constructor(message: string, data: PdfFetchErrorData) {
    super(message);
    this.name = "PdfFetchError";
    this.data = data;
  }
}

const PDF_HEADER = "%PDF-";

const isBlobUrl = (url: string): boolean => url.startsWith("blob:");

/**
 * Best-effort detection of an S3 pre-signed URL.
 *
 * We intentionally avoid hard-coding bucket hostnames; instead we check for the
 * standard SigV4 query params.
 */
export const isPresignedS3Url = (url: string): boolean => {
  try {
    const u = new URL(url);
    const params = u.searchParams;
    return (
      params.has("X-Amz-Signature") &&
      params.has("X-Amz-Date") &&
      params.has("X-Amz-Expires") &&
      params.has("X-Amz-Credential")
    );
  } catch {
    return false;
  }
};

/**
 * Computes the expiry time (epoch ms) for a pre-signed URL if possible.
 * Returns undefined if the URL doesn't look like a pre-signed URL or parsing fails.
 */
export const getPresignedUrlExpiresAt = (url: string): number | undefined => {
  try {
    const u = new URL(url);
    const amzDate = u.searchParams.get("X-Amz-Date");
    const amzExpires = u.searchParams.get("X-Amz-Expires");
    if (!amzDate || !amzExpires) return undefined;

    // X-Amz-Date format: YYYYMMDDThhmmssZ (e.g. 20260120T011353Z)
    const amzDateRegex = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;
    const match = amzDateRegex.exec(amzDate);
    if (!match) return undefined;

    const [, year, month, day, hour, minute, second] = match;
    const issuedAtMs = Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
    const expiresSeconds = Number(amzExpires);
    if (!Number.isFinite(expiresSeconds)) return undefined;
    return issuedAtMs + expiresSeconds * 1000;
  } catch {
    return undefined;
  }
};

const bytesStartWithPdfHeader = (bytes: Uint8Array): boolean => {
  if (bytes.length < PDF_HEADER.length) return false;
  for (let i = 0; i < PDF_HEADER.length; i++) {
    const expected = PDF_HEADER.codePointAt(i) ?? 0;
    if (bytes[i] !== expected) {
      return false;
    }
  }
  return true;
};

const decodeSnippet = (bytes: Uint8Array, maxBytes: number): string => {
  try {
    const slice = bytes.slice(0, Math.min(bytes.length, maxBytes));
    // Use TextDecoder when available for better performance.
    const decoder = new TextDecoder("utf-8", { fatal: false });
    return decoder.decode(slice).slice(0, maxBytes);
  } catch {
    return "";
  }
};

const readResponseTextSnippet = async (response: Response, maxBytes: number, signal?: AbortSignal): Promise<string> => {
  try {
    if (!response.body) {
      const text = await response.text();
      return text.slice(0, maxBytes);
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    const timeoutMs = 5000; // 5 second timeout for reading response snippet
    const timeoutId = signal
      ? undefined
      : setTimeout(() => {
          reader.cancel().catch(() => {
            // ignore
          });
        }, timeoutMs);

    try {
      while (received < maxBytes) {
        if (signal?.aborted) {
          break;
        }
        const result = await reader.read();
        if (result.done) break;
        if (result.value) {
          chunks.push(result.value);
          received += result.value.length;
        }
      }
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      reader.cancel().catch(() => {
        // ignore
      });
    }

    const merged = new Uint8Array(Math.min(received, maxBytes));
    let offset = 0;
    for (const chunk of chunks) {
      const toCopy = Math.min(chunk.length, merged.length - offset);
      if (toCopy <= 0) break;
      merged.set(chunk.slice(0, toCopy), offset);
      offset += toCopy;
    }

    return decodeSnippet(merged, maxBytes);
  } catch {
    return "";
  }
};

export interface FetchPdfBytesOptions {
  signal?: AbortSignal;
  /**
   * Maximum bytes of response body to include in error messages for non-PDF responses.
   * Keep small to avoid memory overhead.
   */
  errorSnippetBytes?: number;
}

/**
 * Fetches a PDF as an ArrayBuffer with validation.
 *
 * - If the URL looks like a pre-signed S3 URL, uses `cache: "no-store"` to reduce
 *   the chance of cached 403 bodies causing persistent failures in a profile.
 * - Throws PdfFetchError for HTTP errors or non-PDF responses.
 */
export const fetchPdfArrayBuffer = async (url: string, options: FetchPdfBytesOptions = {}): Promise<ArrayBuffer> => {
  const { signal, errorSnippetBytes = 1024 } = options;
  const presigned = isPresignedS3Url(url);

  const init: RequestInit = {
    signal,
    ...(presigned && !isBlobUrl(url) ? { cache: "no-store" as const } : {}),
  };

  const response = await fetch(url, init);
  const contentType = response.headers.get("content-type") ?? undefined;

  if (!response.ok) {
    const snippet = await readResponseTextSnippet(response, errorSnippetBytes, signal);
    throw new PdfFetchError(`HTTP ${response.status}: ${response.statusText}`, {
      url,
      status: response.status,
      statusText: response.statusText,
      contentType,
      bodySnippet: snippet || undefined,
      isPresignedS3Url: presigned,
    });
  }

  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  // Accept either a PDF content-type OR a %PDF- header. Some servers return octet-stream.
  const contentTypeLooksPdf = (contentType ?? "").toLowerCase().includes("pdf");
  if (!contentTypeLooksPdf && !bytesStartWithPdfHeader(bytes)) {
    const snippet = decodeSnippet(bytes, errorSnippetBytes);
    throw new PdfFetchError("Response was not a PDF document", {
      url,
      contentType,
      bodySnippet: snippet || undefined,
      isPresignedS3Url: presigned,
    });
  }

  // Even with application/pdf, missing the header will later surface as confusing parse errors.
  if (!bytesStartWithPdfHeader(bytes)) {
    const snippet = decodeSnippet(bytes, errorSnippetBytes);
    throw new PdfFetchError("PDF header was missing from response", {
      url,
      contentType,
      bodySnippet: snippet || undefined,
      isPresignedS3Url: presigned,
    });
  }

  return arrayBuffer;
};

